/**
 * Contextual shimmer skeletons for the Workspace / BuzzTalk module.
 * Pure layout placeholders — no user-facing copy (i18n ratchet).
 */
import { Skeleton, SkeletonAvatar } from '../ui/Skeleton';
import { cn } from '../../lib/utils';

/** Conversation / group list rows while groups bootstrap. */
export function GroupListSkeleton({ rows = 6 }: { rows?: number }): JSX.Element {
  return (
    <div className="inbox-list-scroll" aria-hidden role="presentation">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-start gap-2.5 border-b border-[var(--bx-border)] px-3 py-3"
        >
          <SkeletonAvatar size="md" className="h-9 w-9" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Skeleton className={cn('h-3.5', i % 3 === 0 ? 'w-28' : i % 3 === 1 ? 'w-36' : 'w-24')} />
              <Skeleton className="h-3 w-8 shrink-0" />
            </div>
            <Skeleton className={cn('h-3', i % 2 === 0 ? 'w-4/5' : 'w-3/5')} />
            <div className="flex gap-1.5 pt-0.5">
              <Skeleton className="h-4 w-12 rounded-full" />
              <Skeleton className="h-4 w-16 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Incoming / outgoing bubble placeholders for an empty thread load. */
export function MessageThreadSkeleton({ count = 5 }: { count?: number }): JSX.Element {
  const widths = ['w-[55%]', 'w-[42%]', 'w-[68%]', 'w-[38%]', 'w-[52%]', 'w-[60%]'];
  return (
    <div
      className="flex flex-1 flex-col gap-3 py-2"
      aria-hidden
      role="presentation"
    >
      {Array.from({ length: count }).map((_, i) => {
        const mine = i % 3 === 1;
        return (
          <div
            key={i}
            className={cn('flex flex-col gap-1', mine ? 'items-end' : 'items-start')}
          >
            {!mine ? <Skeleton className="mb-0.5 h-2.5 w-16" /> : null}
            <Skeleton
              className={cn(
                'h-14 rounded-[10px]',
                widths[i % widths.length],
                mine ? 'rounded-br-sm' : 'rounded-bl-sm',
              )}
            />
            <Skeleton className="h-2 w-10" />
          </div>
        );
      })}
    </div>
  );
}

/** Thread header while members / presence resolve. */
export function ThreadHeaderSkeleton(): JSX.Element {
  return (
    <div
      className="inbox-thread-head"
      aria-hidden
      role="presentation"
      style={{ flexWrap: 'wrap', gap: 10 }}
    >
      <SkeletonAvatar size="md" className="h-8 w-8" />
      <div className="min-w-[120px] flex-1 space-y-2">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-2.5 w-24" />
      </div>
      <Skeleton className="h-8 w-36 rounded-lg" />
      <div className="flex gap-1">
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
  );
}

/** Context / details panel body. */
export function ContextPaneSkeleton(): JSX.Element {
  return (
    <div className="space-y-4 p-4" aria-hidden role="presentation">
      <div className="mb-2 flex items-center gap-2.5">
        <SkeletonAvatar size="lg" className="h-11 w-11" />
        <div className="space-y-2">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-4/5" />
      <div className="space-y-2 border-t border-[var(--bx-border)] pt-3">
        <Skeleton className="mb-2 h-3 w-16" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <SkeletonAvatar size="sm" className="h-6 w-6" />
            <Skeleton className="h-3 flex-1" />
            <Skeleton className="h-3 w-10" />
          </div>
        ))}
      </div>
      <div className="space-y-2 border-t border-[var(--bx-border)] pt-3">
        <Skeleton className="mb-2 h-3 w-20" />
        <Skeleton className="h-9 w-full rounded-md" />
      </div>
    </div>
  );
}

/** Docs / Members sidebar page list. */
export function WikiListSkeleton({ rows = 5 }: { rows?: number }): JSX.Element {
  return (
    <ul className="space-y-1" aria-hidden role="presentation">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="flex items-center gap-2 px-2 py-1.5">
          <Skeleton className="h-3.5 w-3.5 shrink-0 rounded" />
          <Skeleton className={cn('h-3.5', i % 2 === 0 ? 'w-32' : 'w-24')} />
        </li>
      ))}
    </ul>
  );
}

/** Docs / Members editor while a selected page loads. */
export function WikiEditorSkeleton(): JSX.Element {
  return (
    <div className="w-full space-y-4" aria-hidden role="presentation">
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 flex-1" />
        <Skeleton className="h-6 w-16 shrink-0" />
        <Skeleton className="h-6 w-6 shrink-0 rounded" />
      </div>
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-64 w-full rounded-md" />
    </div>
  );
}

/** Tasks board columns while tasks fetch. */
export function TasksBoardSkeleton(): JSX.Element {
  return (
    <div
      className="grid flex-1 grid-cols-1 gap-3.5 overflow-auto p-6 md:grid-cols-3"
      style={{ background: 'var(--bx-bg-subtle)' }}
      aria-hidden
      role="presentation"
    >
      {Array.from({ length: 3 }).map((_, col) => (
        <div key={col} className="flex flex-col gap-2.5">
          <Skeleton className="mb-1 h-3 w-16" />
          {Array.from({ length: 2 + (col % 2) }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg border border-[var(--bx-border)] bg-[var(--bx-white)] p-3 space-y-2"
            >
              <Skeleton className="h-3.5 w-4/5" />
              <Skeleton className="h-3 w-2/5" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Compact rows for embed search / member picker. */
export function SearchResultsSkeleton({ rows = 4 }: { rows?: number }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 p-2" aria-hidden role="presentation">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-2 px-2 py-1.5">
          <SkeletonAvatar size="sm" className="h-5 w-5" />
          <div className="min-w-0 flex-1 space-y-1">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-2.5 w-40 max-w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Attachment chip placeholders while upload completes. */
export function AttachmentUploadSkeleton(): JSX.Element {
  return (
    <div className="mb-2 flex flex-wrap gap-2" aria-hidden role="presentation">
      <Skeleton className="h-7 w-28 rounded-md" />
    </div>
  );
}
